import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { fx } from './motion-control.js';
import setupReveal, { ATTR_SCOPE, ATTR_SPLIT, collectScopes } from './reveal.js';
import setupParallax, { resolveTargets as resolveParallaxTargets } from './parallax.js';
import { setupSplitTrigger, collectSplitTargets } from './split-text.js';

const SCROLL_EFFECTS_SELECTOR = `[${ATTR_SCOPE}], [data-parallax], [${ATTR_SPLIT}]`;

class ScrollEffectsManager {
    constructor() {
        this.scopeCleanups = new Map();
        this.parallaxCleanups = new Set();
        this.mediaContext = fx.matchMedia();
    }

    init() {
        this.destroy();

        const scopes = collectScopes();
        scopes.forEach((scope) => this.registerScope(scope));

        this.ensureParallaxOrphans();
        this.syncWindowApi();
    }

    destroy() {
        this.scopeCleanups.forEach((cleanup) => cleanup?.());
        this.scopeCleanups.clear();
        this.parallaxCleanups.forEach((cleanup) => cleanup?.());
        this.parallaxCleanups.clear();
        this.mediaContext?.revert?.();
        this.mediaContext = fx.matchMedia();
        this.syncWindowApi();
    }

    registerScope(scope) {
        if (!scope) return;
        this.cleanupScope(scope);
        const cleanup = this.setupScope(scope);
        if (cleanup) {
            this.scopeCleanups.set(scope, cleanup);
        }
    }

    cleanupScope(scope) {
        const cleanup = this.scopeCleanups.get(scope);
        if (cleanup) {
            cleanup();
            this.scopeCleanups.delete(scope);
        }
    }

    resetScope(scope) {
        const element = typeof scope === 'string' ? document.querySelector(scope) : scope;
        if (!element) return;
        this.registerScope(element);
        this.syncWindowApi();
    }

    setupScope(scope) {
        const cleanups = [];

        const revealTween = setupReveal(scope);
        if (revealTween?.scrollTrigger) {
            cleanups.push(() => revealTween?.scrollTrigger?.kill());
        }

        const splitTargets = collectSplitTargets(scope);
        splitTargets.forEach((target) => {
            const trigger = setupSplitTrigger(target);
            if (trigger) {
                cleanups.push(() => trigger.kill());
            }
        });

        const parallaxCleaners = setupParallax(scope, this.mediaContext);
        parallaxCleaners.forEach((cleanup) => cleanups.push(cleanup));

        return () => cleanups.forEach((cleanup) => cleanup?.());
    }

    registerParallaxTargets(targets) {
        if (!targets || !targets.length) return;
        const cleanups = setupParallax(targets, this.mediaContext);
        cleanups.forEach((cleanup) => {
            if (typeof cleanup === 'function') {
                this.parallaxCleanups.add(cleanup);
            }
        });
    }

    resolveOrphanParallax(scopes) {
        const allParallax = resolveParallaxTargets(document);
        if (!allParallax.length) return [];

        const handled = new Set();
        scopes.forEach((scope) => {
            resolveParallaxTargets(scope).forEach((el) => handled.add(el));
        });

        return allParallax.filter((el) => !handled.has(el));
    }

    ensureParallaxOrphans() {
        const scopes = Array.from(this.scopeCleanups.keys());
        const orphans = this.resolveOrphanParallax(scopes);
        if (orphans.length) {
            this.registerParallaxTargets(orphans);
        }
    }

    syncWindowApi() {
        if (typeof window === 'undefined') return;

        const anim = (window.Anim = window.Anim || {});
        const scroll = (anim.scroll = anim.scroll || {});

        scroll.instances = [
            ...Array.from(this.scopeCleanups.values()),
            ...Array.from(this.parallaxCleanups.values()),
        ];
        scroll.destroy = this.destroy.bind(this);
        scroll.init = this.init.bind(this);
        scroll.resetScope = this.resetScope.bind(this);
        scroll.killAll = () => ScrollTrigger.killAll();
        scroll.refresh = () => ScrollTrigger.refresh();
        scroll.update = () => ScrollTrigger.update();
    }
}

const manager = new ScrollEffectsManager();

export const init = () => manager.init();
export const destroy = () => manager.destroy();

export default {
    init,
    destroy,
};

export { ATTR_SCOPE, ATTR_SPLIT, SCROLL_EFFECTS_SELECTOR };
